import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@App/Components/Funnel/Button";

interface LoginFormProps {
  onSubmit: (
    email: string,
    password: string,
  ) => null | Promise<{ emailError?: string; passwordError?: string }>;
}

// A simple email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginForm({ onSubmit }: LoginFormProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError(null); // Reset email error
    setPasswordError(null); // Reset password error

    // Email validation
    if (!email) {
      setEmailError(t("login.validation.emailRequired", t("login.validations.email.required")));
      isValid = false;
    } else if (!EMAIL_REGEX.test(email)) {
      setEmailError(t("login.validation.emailInvalid", t("login.validations.email.invalid")));
      isValid = false;
    }

    // Password validation
    if (!password) {
      setPasswordError(
        t("login.validation.passwordRequired", t("login.validations.password.required")),
      );
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); // Prevent default form submission
    if (validateForm()) {
      if (!email || !password) {
        return; // Should not happen due to validation
      }
      const result = await onSubmit(email, password);
      if (result) {
        if (result.emailError) {
          setEmailError(result.emailError);
        }
        if (result.passwordError) {
          setPasswordError(result.passwordError);
        }
      }
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError(null); // Clear error when user starts typing
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (passwordError) {
      setPasswordError(null); // Clear error when user starts typing
    }
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 font-serif">
            {t("login.title")}
          </h2>
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-10 text-center text-l text-gray-900">{t("login.subtitle")}</h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {" "}
            {/* Added noValidate to prevent browser default validation popups */}
            <div>
              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                {t("login.eMail")}
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="email"
                  required // Keep for accessibility and non-JS fallback
                  autoComplete="email"
                  className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary-600 sm:text-sm/6 ${
                    emailError ? "outline-error-500 focus:outline-error-500" : "outline-gray-300"
                  }`}
                  value={email || ""}
                  onChange={handleEmailChange}
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? "email-error" : undefined}
                />
              </div>
              {emailError && (
                <p id="email-error" className="mt-2 text-sm text-error-600" role="alert">
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">
                  {t("login.password")}
                </label>
                {/*<div className="text-sm">*/}
                {/*    <a href="#" className="font-semibold text-primary-600 hover:text-primary-500">*/}
                {/*        Forgot password?*/}
                {/*    </a>*/}
                {/*</div>*/}
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="password"
                  required // Keep for accessibility and non-JS fallback
                  autoComplete="current-password"
                  className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary-600 sm:text-sm/6 ${
                    passwordError ? "outline-error-500 focus:outline-error-500" : "outline-gray-300"
                  }`}
                  value={password || ""}
                  onChange={handlePasswordChange}
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? "password-error" : undefined}
                />
              </div>
              {passwordError && (
                <p id="password-error" className="mt-2 text-sm text-error-600" role="alert">
                  {passwordError}
                </p>
              )}
            </div>
            <div>
              <Button title={t("login.signIn")} type={"submit"} width={"md:w-full"} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
